import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import clientAuthRequest from '@fingerprinting/api/client/auth'
import clientAuthUtils from '@fingerprinting/lib/auth.utils'

import type { TClientUserSchema, TClientLoginFormValues, TClientAuthResponse } from '@fingerprinting/types/schemas/auth.schema'

interface ClientAuthState {
  user: TClientUserSchema | null
  isAuthenticated: boolean
  isLoading: boolean
  requiresDobConfirmation: boolean
  error: {
    message: string
    code?: string
  } | null

  login: (data: TClientLoginFormValues) => Promise<TClientAuthResponse & { requiresDobConfirmation?: boolean }>
  confirmDob: (data: { dateOfBirth: string }) => Promise<TClientAuthResponse>
  setRequiresDobConfirmation: (value: boolean) => void
  logout: () => Promise<void>
  clearError: () => void
}

export const useClientAuthStore = create<ClientAuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      requiresDobConfirmation: false,
      error: null,

      login: async (data: TClientLoginFormValues) => {
        set({ isLoading: true, error: null })
        try {
          const response = await clientAuthRequest.login(data)
          
          if (!response.data) {
            throw new Error(response.message || 'Login failed')
          }

          const authData = response.data
          const requiresDobConfirmation = response.requiresDobConfirmation ?? false
          const { token, userName, ...rest } = authData

          const userData: TClientUserSchema = {
            ...rest,
            username: userName,
          }

          clientAuthUtils.setAccessToken(token)
          clientAuthUtils.setClientUser(userData)

          set({
            user: userData,
            isAuthenticated: true,
            isLoading: false,
            requiresDobConfirmation,
          })

          return { ...authData, requiresDobConfirmation }
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.'
          set({
            isLoading: false,
            error: {
              message: errorMessage,
            },
          })
          throw error
        }
      },

      confirmDob: async (data: { dateOfBirth: string }) => {
        set({ isLoading: true, error: null })
        try {
          const response = await clientAuthRequest.confirmDob(data)
          
          if (!response.data) {
            throw new Error(response.message || 'Confirm DOB failed')
          }

          const authData = response.data
          const { token, userName, ...rest } = authData

          const userData: TClientUserSchema = {
            ...rest,
            username: userName,
          }

          clientAuthUtils.setAccessToken(token)
          clientAuthUtils.setClientUser(userData)

          set({
            user: userData,
            isAuthenticated: true,
            isLoading: false,
            requiresDobConfirmation: false,
          })

          return authData
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Confirm DOB failed. Please try again.'
          set({
            isLoading: false,
            error: {
              message: errorMessage,
            },
          })
          throw error
        }
      },

      setRequiresDobConfirmation: (value: boolean) => set({ requiresDobConfirmation: value }),

      logout: async () => {
        try {
          await clientAuthRequest.logout()
        } catch {
          // Best-effort server logout; always clear local session.
        }
        clientAuthUtils.clearAuth()
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          requiresDobConfirmation: false,
          error: null,
        })
        useClientAuthStore.persist.clearStorage()
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'fingerprinting-auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated,
        requiresDobConfirmation: state.requiresDobConfirmation,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version < 2) {
          return { ...persistedState, requiresDobConfirmation: false }
        }
        return persistedState
      },
      version: 2,
    },
  ),
)
